import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { Schedule } from '@mantine/schedule';
import { IconAlertCircle, IconCalendarEvent } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import {
  SCHEDULE_LABELS_FR,
  crmEntryToScheduleEvent,
  getPlanningRange,
} from '../../lib/planningSchedule';
import { authFetch, parseJsonResponse } from '../../lib/api';

dayjs.locale('fr');

const STATUS_LABELS = {
  pending: 'En attente de votre réponse',
  accepted: 'Accepté',
  declined: 'Refusé',
};

const STATUS_COLORS = {
  pending: 'orange',
  accepted: 'teal',
  declined: 'red',
};

function formatDateTime(value) {
  if (!value) return '—';
  const d = dayjs(value);
  if (!d.isValid()) return '—';
  return d.format('dddd D MMMM YYYY [à] HH:mm');
}

export default function ClientPortalPlanningPanel({
  refreshKey = 0,
  onRdvResponded,
  focusRdv = null,
  onFocusRdvHandled,
  exploitantLabel = "l'exploitant",
}) {
  const [view, setView] = useState('week');
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [responding, setResponding] = useState(false);
  const [savingMessage, setSavingMessage] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);

  const scheduleEvents = useMemo(
    () => entries.map((entry) => crmEntryToScheduleEvent(entry)),
    [entries],
  );

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { from, to } = getPlanningRange(date, view);
      const params = new URLSearchParams({
        from: from.format('YYYY-MM-DD'),
        to: to.format('YYYY-MM-DD'),
      });
      const res = await authFetch(`/api/client-portal/rdv?${params.toString()}`);
      const data = await parseJsonResponse(res);
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Chargement impossible');
      }
      setEntries(data.data || []);
    } catch (err) {
      setError(err.message || 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [date, view]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries, refreshKey]);

  useEffect(() => {
    if (!focusRdv?.id) return;
    const day = dayjs(focusRdv.occurred_at);
    if (day.isValid()) {
      setDate(day.format('YYYY-MM-DD'));
      setView('week');
    }
    onFocusRdvHandled?.();
  }, [focusRdv?.id, focusRdv?.occurred_at, onFocusRdvHandled]);

  useEffect(() => {
    if (!selectedEvent) {
      setResponseMessage('');
      setShowDeclineConfirm(false);
      return;
    }
    const payload = selectedEvent.payload || {};
    setResponseMessage(payload.client_rdv_message || '');
    setShowDeclineConfirm(false);
  }, [selectedEvent]);

  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const closeModal = () => {
    setSelectedEvent(null);
    setShowDeclineConfirm(false);
  };

  const handleSaveMessage = async () => {
    if (!selectedEvent?.id) return;
    const trimmed = responseMessage.trim();
    if (!trimmed) {
      notifications.show({
        title: 'Message vide',
        message: 'Saisissez un message avant d’envoyer.',
        color: 'orange',
      });
      return;
    }
    setSavingMessage(true);
    try {
      const res = await authFetch(`/api/client-portal/rdv/${selectedEvent.id}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: trimmed }),
      });
      const data = await parseJsonResponse(res);
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Envoi impossible');
      }
      notifications.show({ title: 'Message envoyé', color: 'teal' });
      if (data.data) {
        setSelectedEvent(crmEntryToScheduleEvent(data.data));
        setResponseMessage(data.data.client_rdv_message || '');
      }
      await loadEntries();
      onRdvResponded?.();
    } catch (err) {
      notifications.show({
        title: 'Erreur',
        message: err.message || 'Envoi impossible',
        color: 'red',
      });
    } finally {
      setSavingMessage(false);
    }
  };

  const handleRespond = async (action) => {
    if (!selectedEvent?.id) return;
    setResponding(true);
    try {
      const res = await authFetch(`/api/client-portal/rdv/${selectedEvent.id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: responseMessage.trim() || null }),
      });
      const data = await parseJsonResponse(res);
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Action impossible');
      }
      notifications.show({
        title: action === 'accept' ? 'RDV accepté' : 'RDV refusé',
        color: action === 'accept' ? 'teal' : 'orange',
      });
      closeModal();
      await loadEntries();
      onRdvResponded?.();
    } catch (err) {
      notifications.show({
        title: 'Erreur',
        message: err.message || 'Action impossible',
        color: 'red',
      });
    } finally {
      setResponding(false);
      setShowDeclineConfirm(false);
    }
  };

  const payload = selectedEvent?.payload || {};
  const status = payload.client_rdv_status;
  const savedMessage = payload.client_rdv_message;
  const isFuture = selectedEvent ? dayjs(selectedEvent.start).isAfter(dayjs()) : false;
  const canExchange = status === 'pending';
  const canRespond = canExchange && isFuture;
  const hasResponded = status === 'accepted' || status === 'declined';

  return (
    <Stack gap="md">
      {error ? (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Erreur">
          {error}
        </Alert>
      ) : null}

      <Box style={{ minHeight: 480 }}>
        {loading && entries.length === 0 ? (
          <Group justify="center" py="xl">
            <Loader type="dots" />
          </Group>
        ) : (
          <Schedule
            date={date}
            onDateChange={setDate}
            view={view}
            onViewChange={setView}
            events={scheduleEvents}
            locale="fr"
            labels={SCHEDULE_LABELS_FR}
            layout="responsive"
            defaultView="week"
            onEventClick={handleEventClick}
            weekViewProps={{
              firstDayOfWeek: 1,
              startTime: '07:00:00',
              endTime: '20:00:00',
            }}
            dayViewProps={{
              startTime: '07:00:00',
              endTime: '20:00:00',
            }}
            monthViewProps={{ firstDayOfWeek: 1 }}
          />
        )}
      </Box>

      <Modal
        opened={Boolean(selectedEvent)}
        onClose={closeModal}
        title={
          <Group gap="xs">
            <IconCalendarEvent size={18} />
            <Text fw={600}>Rendez-vous</Text>
          </Group>
        }
        centered
        size="md"
      >
        {selectedEvent ? (
          <Stack gap="md">
            <Text size="sm">
              <Text span fw={600}>
                Date :
              </Text>{' '}
              {formatDateTime(selectedEvent.start)}
            </Text>
            {payload.infos ? (
              <Text size="sm">
                <Text span fw={600}>
                  Informations :
                </Text>{' '}
                {payload.infos}
              </Text>
            ) : null}
            {status ? (
              <Badge color={STATUS_COLORS[status] || 'gray'} variant="light">
                {STATUS_LABELS[status] || status}
              </Badge>
            ) : null}

            {hasResponded && savedMessage ? (
              <Textarea
                label="Votre message"
                value={savedMessage}
                readOnly
                minRows={2}
                autosize
                disabled
              />
            ) : null}

            {canExchange ? (
              <>
                <Textarea
                  label={`Votre message pour ${exploitantLabel}`}
                  description="Proposez un autre créneau, posez une question ou précisez votre refus."
                  placeholder="Ex. : ce créneau ne me convient pas, seriez-vous disponible le…"
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.currentTarget.value)}
                  minRows={3}
                  autosize
                />

                <Group justify="flex-start" wrap="wrap" gap="sm">
                  <Button variant="default" loading={savingMessage} onClick={handleSaveMessage}>
                    Envoyer le message
                  </Button>
                </Group>

                {canRespond && !showDeclineConfirm ? (
                  <Group justify="flex-end" gap="sm">
                    <Button
                      variant="default"
                      color="red"
                      onClick={() => setShowDeclineConfirm(true)}
                    >
                      Refuser
                    </Button>
                    <Button loading={responding} onClick={() => handleRespond('accept')}>
                      Accepter
                    </Button>
                  </Group>
                ) : null}

                {canRespond && showDeclineConfirm ? (
                  <Alert color="red" title="Confirmer le refus ?">
                    <Stack gap="sm">
                      <Text size="sm">
                        Le rendez-vous sera marqué comme refusé. Votre message sera transmis
                        transmis à {exploitantLabel}.
                      </Text>
                      <Group justify="flex-end" gap="sm">
                        <Button variant="default" onClick={() => setShowDeclineConfirm(false)}>
                          Annuler
                        </Button>
                        <Button
                          color="red"
                          loading={responding}
                          onClick={() => handleRespond('decline')}
                        >
                          Confirmer le refus
                        </Button>
                      </Group>
                    </Stack>
                  </Alert>
                ) : null}
              </>
            ) : null}

            {canExchange && !canRespond ? (
              <Text size="sm" c="dimmed">
                Ce créneau est passé : vous pouvez encore envoyer un message, mais plus accepter ou
                refuser en ligne.
              </Text>
            ) : null}
          </Stack>
        ) : null}
      </Modal>
    </Stack>
  );
}
